import { Box, Typography } from '@mui/material';
import { DataGridPro, GridCellParams, GridColDef, GridValueGetterParams } from '@mui/x-data-grid-pro';
import React from 'react';
import { connect } from 'react-redux';
import { RouteComponentProps, withRouter } from 'react-router';

import { GeneralConstants } from 'app/constants/general-constants';
import { StoreMetric } from 'app/model/metrics/store-metric';
import { RootState } from 'app/store';
import { DataGridFormatters } from 'app/utils/data-grid-formatters';
import { Formatters } from 'app/utils/formatters';

import { MetricsGraph } from './metrics-graph';
import { loadStoreGraphData, loadStoreMetrics, selectStoreGraphMetric, selectStoreMetric } from '../slice';

interface StoreMetricsProps extends RouteComponentProps<{ date?: string }> {
    // redux props
    metrics?: StoreMetric[];
    metricsGraph?: StoreMetric[];

    // redux actions
    loadStoreMetrics: (date: string) => void;

    loadStoreGraphData: (storeId: number) => void;
}

interface StoreMetricsState {
    graphField?: string;
    graphLabel?: string;
    date?: string;
}

class StoreMetrics extends React.Component<StoreMetricsProps, StoreMetricsState> {
    public constructor(props: Readonly<StoreMetricsProps> | StoreMetricsProps) {
        super(props);

        this.state = {};
    }

    public componentDidMount() {
        const pararms = new URLSearchParams(this.props.location.search);
        const date = pararms.get('date');
        if (!date) {
            this.props.history.replace('/seam/metrics');

            return;
        }

        this.props.loadStoreMetrics(date);
        this.setState({ date });
    }

    public render(): React.ReactNode {
        const columns: Array<GridColDef<StoreMetric>> = [
            {
                field: 'id',
                headerName: 'ID',
                width: 150,
            },
            {
                field: 'metricDate',
                headerName: 'Metric Month',
                width: 170,
                valueFormatter: DataGridFormatters.monthYearFormatter,
            },
            {
                field: 'store',
                headerName: 'Store',
                width: 170,
                disableColumnMenu: true,
                valueGetter: (params: GridValueGetterParams<StoreMetric, StoreMetric['store']>) => params.value?.name,
            },
            {
                field: 'brand',
                headerName: 'Brand',
                width: 170,
                disableColumnMenu: true,
                valueGetter: (params) => params.row.store.brand?.name,
            },
            {
                field: 'category',
                headerName: 'Category',
                width: 170,
                valueGetter: (params) => params.row.store.brand?.category?.category,
            },
            {
                field: 'activeCards',
                headerName: '# of Active Cards',
                width: 170,
                disableColumnMenu: true,
            },
            {
                field: 'totalSpend',
                headerName: 'Spend',
                width: 150,
            },
            {
                field: 'transactionCount',
                headerName: 'Transactions',
                width: 170,
            },
        ];

        return (
            <Box display='flex' flexDirection='column' flexGrow={1}>
                <Typography variant='h4'>Store Metrics For {Formatters.monthYearFormatter(this.state.date)}</Typography>
                <Typography variant='body1'>Click on a metric to see 3 year graph for that store. stores.</Typography>
                {this.renderGraph()}
                <Box flexGrow={1}>
                    <Box sx={{ width: '100%', display: 'grid', height: '100%' }}>
                        <DataGridPro
                            columns={columns}
                            rows={this.props.metrics || []}
                            loading={undefined === this.props.metrics}
                            density='compact'
                            columnVisibilityModel={{
                                id: false,
                                metricDate: false,
                            }}
                            onCellClick={(params: GridCellParams<StoreMetric>) => {
                                const field = params.field;
                                if ('brand' === field || 'category' === field || 'store' === field) {
                                    return;
                                }

                                const store = params.row.store;
                                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                                const header = params.colDef.headerName!;

                                this.setState({ graphField: field, graphLabel: `${header} for ${store.name}` });
                                this.props.loadStoreGraphData(store.id);
                            }}
                        />
                    </Box>
                </Box>
            </Box>
        );
    }

    private renderGraph() {
        if (!this.state.graphField) {
            return GeneralConstants.RENDER_NOTHING;
        }

        return (
            <MetricsGraph
                label={this.state.graphLabel || ''}
                data={this.props.metricsGraph || []}
                x='metricDate'
                y={this.state.graphField}
            />
        );
    }
}

const mapStateToProps = (state: RootState) => ({
    metrics: selectStoreMetric(state),
    metricsGraph: selectStoreGraphMetric(state),
});

const thunks = { loadStoreMetrics, loadStoreGraphData };

const enhanced = withRouter(connect(mapStateToProps, thunks)(StoreMetrics));

export { enhanced as StoreMetrics };
